import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export interface LanguageResponse {
    id: number;
    language?: string | null;
    display_name?: string | null;
    code?: string | null;
    is_active?: boolean;
    [key: string]: unknown;
}

export interface LanguageListResponse {
    languages: LanguageResponse[];
    total: number;
}

const normalizeLanguageListResponse = (payload: unknown): LanguageListResponse => {
    if (Array.isArray(payload)) {
        return {
            languages: payload,
            total: payload.length,
        };
    }

    if (payload && typeof payload === 'object') {
        const response = payload as Partial<LanguageListResponse> & { data?: unknown };
        if (Array.isArray(response.languages)) {
            return {
                languages: response.languages,
                total: response.total ?? response.languages.length,
            };
        }

        if (Array.isArray(response.data)) {
            return {
                languages: response.data as LanguageResponse[],
                total: response.total ?? response.data.length,
            };
        }
    }

    return {
        languages: [],
        total: 0,
    };
};

export async function getAllLanguages(): Promise<LanguageListResponse> {
    try {
        const response = (await apiClient.get("/language/all")).data;
        return normalizeLanguageListResponse(response);
    } catch (error: any) {
        if (error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to fetch languages");
    }
}

export async function getLanguageById(languageId: number): Promise<LanguageResponse> {
    try {
        const response: ApiResponse<LanguageResponse> = (
            await apiClient.get(`/language/${languageId}`)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error(`Failed to fetch language with ID ${languageId}`);
    }
}

export async function getLanguageByCode(languageCode: string): Promise<LanguageResponse> {
    try {
        const response: ApiResponse<LanguageResponse> = (
            await apiClient.get(`/language/code/${encodeURIComponent(languageCode)}`)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error(`Failed to fetch language with code ${languageCode}`);
    }
}

export async function getLanguageByName(displayName: string): Promise<LanguageResponse> {
    try {
        const response: ApiResponse<LanguageResponse> = (
            await apiClient.get(`/language/name/${encodeURIComponent(displayName)}`)
        ).data;
        return response.data;
    } catch (error: any) {
        if (error.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error(`Failed to fetch language with name ${displayName}`);
    }
}
